import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, ChevronRight } from "lucide-react";
import { useCart } from "../lib/cart";
import { formatINR } from "../lib/utils.fmt";

/**
 * Sticky cart CTA pinned ABOVE the customer bottom-nav on mobile. Hidden on
 * routes where the customer is already inside the cart/checkout flow (no
 * point showing "View cart" while they're literally on it).
 *
 * On desktop (md:) this reverts to a normal in-flow block instead of
 * staying `fixed` — a page whose own content is short (e.g. ProductDetail
 * without much copy) could be entirely within the fixed ribbon's viewport
 * zone at the initial scroll position, so the ribbon visually overlapped
 * that page's own controls (confirmed: the Order Once / Subscribe & Save
 * toggle). Rendered from CustomerLayout.jsx right after the routed page's
 * own content, inside the same width-constrained column, so once static it
 * lines up with that page instead of spanning the full unconstrained shell.
 */
const HIDE_ON = ["/app/checkout"];

export default function CartRibbon() {
  const cart = useCart();
  const { pathname } = useLocation();
  if (!cart || cart.count === 0) return null;
  if (HIDE_ON.some((p) => pathname.startsWith(p))) return null;
  return (
    <Link
      to="/app/checkout"
      data-testid="cart-ribbon"
      className="fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-24px)] max-w-md
                 bottom-[88px]
                 md:static md:translate-x-0 md:w-full md:max-w-none md:mt-6 md:mb-6
                 bg-brand-ink text-white rounded-2xl shadow-2xl shadow-brand-ink/30
                 flex items-center justify-between gap-3 px-4 py-3 active:scale-[0.99] transition"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
          <ShoppingCart className="w-4 h-4" strokeWidth={2.4}/>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-white/70">
            {cart.count} item{cart.count === 1 ? "" : "s"} · {formatINR(cart.total)}
          </div>
          <div className="font-heading font-extrabold leading-tight">View cart</div>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 shrink-0" strokeWidth={2.4}/>
    </Link>
  );
}
